import {
  selectCurrentToken,
  selectCurrentUser,
  selectWallet,
  setToken,
  setUser,
} from '@features';
import { useAppDispatch, useAppSelector } from '@hooks';
import { useChainManager } from '../chain/hooks/useChainManager';
import { useEffect, useState, useRef } from 'react';
import { SupportedChain } from '../constants';

export const useAuth = () => {
  const dispatch = useAppDispatch();
  const user = useAppSelector(selectCurrentUser);
  const token = useAppSelector(selectCurrentToken);

  const wallet = useAppSelector(selectWallet);
  const {
    currentChain,
    wallet: chainWallet,
    connectWallet,
    disconnectWallet,
    signMessage,
  } = useChainManager();

  // Add error state for better error handling
  const [authError, setAuthError] = useState<string | null>(null);
  const [userRejectedConnection, setUserRejectedConnection] = useState(false);

  const [isSigningIn, setIsSigningIn] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  // Fix: hasLogin should only require token and user, wallet is managed separately
  // hasLogin should persist even if wallet gets disconnected temporarily
  const hasLogin = Boolean(token && user);

  // Use ref to track if we're already processing to prevent duplicate calls
  const processingRef = useRef(false);
  const lastAddressRef = useRef<string | undefined>(undefined);
  const addressChangeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Clear token when switching chains - but only if we have a token
  // Only clear if chain actually changed, not on initial load
  const [previousChain, setPreviousChain] = useState<SupportedChain | null>(
    null,
  );
  useEffect(() => {
    if (previousChain && previousChain !== currentChain && token) {
      console.log(
        'useAuth: Clearing token when switching chain from',
        previousChain,
        'to',
        currentChain,
      );
      dispatch(setToken(null));
      window.localStorage.removeItem('wallet_signed_token');
    }
    setPreviousChain(currentChain as SupportedChain);
  }, [currentChain, dispatch, token, previousChain]);

  // Monitor wallet state changes from ChainManager
  useEffect(() => {
    // Only log if address actually changed
    const currentAddress = chainWallet?.address;
    const lastAddress = lastAddressRef.current;

    // Clear any existing timeout
    if (addressChangeTimeoutRef.current) {
      clearTimeout(addressChangeTimeoutRef.current);
    }

    // Only log if address actually changed
    if (currentAddress !== lastAddress) {
      // Debounce address changes to prevent rapid logging
      addressChangeTimeoutRef.current = setTimeout(() => {
        console.log('useAuth: Wallet address changed:', {
          from: lastAddress,
          to: currentAddress,
          isSigningIn,
          isConnecting,
          currentChain,
        });
        lastAddressRef.current = currentAddress;
      }, 100); // 100ms debounce
    }

    // If wallet gets disconnected while we're in signing state, reset signing state
    if (isSigningIn && !currentAddress) {
      console.log(
        'useAuth: Wallet disconnected during signing, resetting signing state',
      );
      setIsSigningIn(false);
      setIsConnecting(false);
      setAuthError('Wallet connection lost during authentication');
      processingRef.current = false;
    }

    // If wallet gets disconnected after successful authentication, clear user/token
    // This ensures user is logged out when wallet is disconnected
    if (hasLogin && !currentAddress && !isSigningIn && !isConnecting) {
      console.log(
        'useAuth: Wallet disconnected after authentication, clearing user/token',
      );
      // Clear user and token without calling signOut to avoid infinite loop
      dispatch(setUser(null));
      dispatch(setToken(null));
      window.localStorage.removeItem('wallet_signed_token');
    }

    // Cleanup timeout on unmount
    return () => {
      if (addressChangeTimeoutRef.current) {
        clearTimeout(addressChangeTimeoutRef.current);
      }
    };
  }, [chainWallet?.address, isSigningIn, currentChain]); // Only watch address, not entire object

  // Handle wallet connection and token generation
  useEffect(() => {
    const handleWalletConnection = async () => {
      // Prevent duplicate processing
      if (processingRef.current) {
        console.log('useAuth: Already processing, skipping duplicate call');
        return;
      }

      // Only process if we're in signing state and have wallet info
      console.log('useAuth: chainWallet', chainWallet);
      console.log('useAuth: isSigningIn', isSigningIn);
      console.log('useAuth: isConnecting', isConnecting);

      // More specific conditions to prevent race conditions
      if (!isSigningIn) {
        console.log(
          'useAuth: Skipping wallet connection - signing not in progress:',
          { isSigningIn },
        );
        return;
      }

      // Check if we have wallet info from ChainManager
      if (!chainWallet?.address) {
        console.log(
          'useAuth: No wallet address available, waiting for wallet connection',
        );
        return;
      }

      // Set processing flag to prevent duplicates
      processingRef.current = true;

      try {
        console.log(
          'useAuth: Processing wallet connection for chain:',
          currentChain,
        );
        setAuthError(null); // Clear any previous errors

        // Use real message signing for both chains
        console.log('useAuth: Calling signMessage...');
        const signedToken = await signMessage();
        console.log(
          'useAuth: SignMessage completed, token length:',
          signedToken?.length,
        );

        dispatch(setToken(signedToken));
        console.log('useAuth: Generated signed token for', currentChain);
      } catch (error) {
        console.error('useAuth: Failed to generate token:', error);
        console.error('useAuth: Error details:', {
          message: error instanceof Error ? error.message : 'Unknown error',
          stack: error instanceof Error ? error.stack : undefined,
          error,
        });
        const errorMessage =
          error instanceof Error ? error.message : 'Failed to sign message';
        setAuthError(errorMessage);
      } finally {
        setIsSigningIn(false);
        setIsConnecting(false); // Reset connecting state after signing completes
        processingRef.current = false; // Reset processing flag
        console.log(
          'useAuth: Authentication process completed, resetting states',
        );
      }
    };

    // Only run if we're in signing state
    if (isSigningIn) {
      handleWalletConnection();
    }
  }, [isSigningIn, chainWallet?.address, dispatch, signMessage, currentChain]); // Remove isConnecting to prevent loops

  const signIn = async (): Promise<void> => {
    console.log('useAuth: signIn called with current state:', {
      isConnecting,
      isSigningIn,
      processingRef: processingRef.current,
      userRejectedConnection,
    });

    // Prevent multiple simultaneous connection attempts
    if (isConnecting || isSigningIn || processingRef.current) {
      console.warn(
        'useAuth: Connection already in progress, ignoring duplicate request',
      );
      return;
    }

    // Don't auto-connect if user previously rejected
    if (userRejectedConnection) {
      console.warn(
        'useAuth: User previously rejected connection, not auto-connecting',
      );
      setAuthError('Please manually connect your wallet');
      return;
    }

    try {
      console.log('useAuth: Setting isConnecting to true');
      setIsConnecting(true);
      setAuthError(null);
      setUserRejectedConnection(false); // Reset rejection state

      console.log(
        'useAuth: Starting wallet connection for chain:',
        currentChain,
      );

      // Connect wallet first and wait for it to complete
      const walletInfo = await connectWallet();
      console.log('useAuth: Wallet connected successfully:', walletInfo);

      // Wait a bit for the wallet state to propagate to chainWallet
      await new Promise(resolve => setTimeout(resolve, 100));

      // Set signing state to trigger token generation
      console.log('useAuth: Setting isSigningIn to true');
      setIsSigningIn(true);

      // Don't set isConnecting to false here - let the signing process handle it
      console.log(
        'useAuth: Signing process initiated, isConnecting will remain true until signing completes',
      );
    } catch (error) {
      console.error('Failed to connect wallet:', error);
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to connect wallet';

      // Check if user rejected the connection
      if (
        errorMessage.includes('User rejected') ||
        errorMessage.includes('user rejected') ||
        errorMessage.includes('User denied') ||
        errorMessage.includes('user denied')
      ) {
        setUserRejectedConnection(true);
        setAuthError('Connection was rejected. Please try again manually.');
      } else {
        setAuthError(errorMessage);
      }

      console.log('useAuth: Error occurred, resetting states');
      setIsSigningIn(false);
      setIsConnecting(false); // Only set to false on error
      processingRef.current = false; // Reset processing flag on error
      throw error;
    }
  };

  const signOut = async (): Promise<void> => {
    console.log('useAuth: Starting signOut process');

    // Clear token from localStorage
    window.localStorage.removeItem('wallet_signed_token');

    // Clear token and user from Redux state
    dispatch(setToken(null));
    dispatch(setUser(null));

    // Disconnect wallet
    await disconnectWallet();

    console.log('useAuth: SignOut completed - user and token cleared');
  };

  return {
    user,
    token,
    signIn,
    signOut,
    wallet: chainWallet, // Use chainWallet instead of Redux wallet
    hasLogin,
    // Error handling states
    authError,
    isConnecting,
    isSigningIn,
    userRejectedConnection,
    clearError: () => {
      setAuthError(null);
      setUserRejectedConnection(false);
    },
  };
};
